import { Injectable } from '@angular/core';
import { FetchDocumentsResult, FetchDocumentsMessage, DocumentIdentifier, SourceDocumentIdentifier, DocumentCreateVersionFile, RoutingDetails } from './document.model';
import { DeleteDocumentResult, DeleteSourceDocumentResult } from './document.model';
import { HttpClient } from '@angular/common/http';
import { ErrorHandlerService } from '../error-handler.service';
import { ErrorHandlerUnauthorizedService } from '../error-handler-unauthorized.service';
import { LogService } from '../log.service';
import { CommandService, api } from '../command.service';
import { UserService } from '../user.service';
import { Remark } from './remarks/remarks.model';
import { LegalHoldStatus } from './legalhold/legalhold.model';
import { DialogService } from '../dialogs/dialog.service';
import { Keywords } from './keywords/keywords.model';
import { parseDate } from '@progress/kendo-angular-intl';
import { Utils } from '../utils'
import { IndexData } from '../data-entry/indexing.model';
import { Subject, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FileInfo } from './file.model';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  deleteApiInprogress = new Subject<string>();

  constructor(private http: HttpClient,
    private error: ErrorHandlerService,
    private log: LogService,
    private command: CommandService,
    private user: UserService,
    private dialogService: DialogService,
    private unauthorized: ErrorHandlerUnauthorizedService) { }

  /**
   * Get an observable to fetch a document. Call subscribe() to invoke.
   * 
   * @param message The parameters for the request
   */
  fetch(message: FetchDocumentsMessage) {
    this.log.logMessage(`fetch(); document count ${message.DocumentsToFetch.length}`);
    message.DocumentsToFetch.forEach(d => {
      this.log.logMessage(`fetch(); ${d}`);
    });
    //
    // Define the http post request. It will not execute until .subscribe() is called.
    //

    return this.http.post<FetchDocumentsResult>(
      // the URL
      this.command.getUrl(api.DocumentsToFetch),
      // body
      message,
      // the parameters
      this.command.fetchDocumentsOptions(this.user.getUser().UniqueId))
      // pipe the observable result from 
      // http.get() through an RxJS catchError() operator
      .pipe(
        // tap into the flow of the observable values
        // this allows us to look at the observable values
        // and do something (e.g. log them).
        tap(result => {

          //
          // Log result
          //
          this.log.logMessage(`fetch(); result document count ${result.DocumentResult.length}; 
          ViewerUrl = ${result.ViewerUrl}; warning = ${result.Warning}`);

          result.DocumentResult.forEach(r => {
            this.log.logMessage(`fetch(); result document master ID: ${r.DocumentFetched.MasterId}`);
            r.UrlPages.forEach(u => this.log.logMessage(`fetch(); URL = ${u.Value}`));
          });
        }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }

  // Remarks

  getRemark(document: DocumentIdentifier) {
    this.log.logMessage(`retrieveRemark(); enter`);
    return this.http.get<Remark>(
      this.command.getUrl(api.DocumentRemarks),
      this.command.retrieveRemarksOptions(this.user.getUser().UniqueId, document))
      .pipe(tap(remarks => {
        this.log.logMessage(`retrieveRemark(); read remarks data
        ${remarks.Content}`);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }

  postRemark(remark: Remark) {
    this.log.logMessage(`updateRemark(); enter`);
    return this.http.post<Remark>(
      // the URL
      this.command.getUrl(api.DocumentRemarks),
      // the Body
      remark,
      // the parpmeters
      this.command.updateRemarkOptions(this.user.getUser().UniqueId))
      .pipe(tap(() => {
        this.log.logMessage(`updateRemark(); updated remarks data ${remark.Content}`);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  /** retrieveKeywords
 * @param document DocumentIdentifier
 */
  getKeywords(document: SourceDocumentIdentifier) {

    this.log.logMessage(`retrieveKeywords(); enter`);

    //
    // Define the http get request. It will not execute until .subscribe() is called.
    //

    return this.http.get<Keywords>(
      // the URL
      this.command.getUrl(api.DocumentKeywords),
      // the parameters
      this.command.retrieveKeywordsOptions(this.user.getUser().UniqueId, document))
      // pipe the observable result from 
      // http.get() through an RxJS catchError() operator
      .pipe(
        // tap into the flow of the observable values
        // this allows us to look at the observable values
        // and do something (e.g. log them).
        tap(result => {

          //
          // Log result
          //
          this.log.logMessage(`retrieveKeywords(); read keyword data
          ${result.KeywordData.toString()}`);

        }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }

  /** createKeywords
   * @param keywords 
   */
  createKeywords(keywords: Keywords) {

    this.log.logMessage(`updateKeywords(); enter`);

    return this.http.post<Keywords>(
      // the URL
      this.command.getUrl(api.DocumentKeywords),
      // body
      keywords,
      // the parameters
      this.command.updateKeywordsOptions(this.user.getUser().UniqueId))
      // pipe the observable result from 
      // http.get() through an RxJS catchError() operator
      .pipe(
        // tap into the flow of the observable values
        // this allows us to look at the observable values
        // and do something (e.g. log them).
        tap(result => {

          //
          // Log result
          //
          this.log.logMessage(`updateKeywords(); updated keyword data 
          ${result.KeywordData}`);
        }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  /**
  * Delete Document
  * @param document - documentidentifier to delete 
  */
  deleteDocument(document: DocumentIdentifier) {

    this.log.logMessage(`document.service.deleteDocument(); enter - ${document.MasterId};${document.Version};${document.Suffix}`);

    this.deleteApiInprogress.next(document.toUniqueId());

    return this.http.delete<DeleteDocumentResult>(
      this.command.getUrl(api.DocumentIndex),
      this.command.deleteDocumentOptions(this.user.getUser().UniqueId, document.MasterId, document.Version, document.Suffix))
      .pipe(
        tap(result => {
          this.log.logMessage(`deleteDocument()`);
        }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return throwError(err);
          return this.error.handleError(err);
        })
      );
  }

  /**
    * Destroy Documents
    * @param documents - array of sorucedocumentidentifiers to delete 
  */
  destroyDocument(document: SourceDocumentIdentifier, allVersions: boolean, priorVersions: boolean) {

    this.log.logMessage(`document.service.destroyDocuments(); enter - ${document.MasterId}:${document.Version}`);

    this.deleteApiInprogress.next(document.toUniqueId());

    return this.http.delete<DeleteSourceDocumentResult>(
      this.command.getUrl(api.Document),
      this.command.destroyDocumentsOptions(this.user.getUser().UniqueId, document.MasterId, document.Version, 0, allVersions, priorVersions))
      .pipe(
        tap(results => {
          this.log.logMessage(`destroyDocuments()`);
        }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return throwError(err);
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Destroy all versions of a document
   * @param masterId Master id to destroy
   */
  getAllVersions(masterId: number) {
    this.log.logMessage(`getAllVersions(); enter`);

    //
    // Define the http get request. It will not execute until .subscribe() is called.
    //

    return this.http.get<SourceDocumentIdentifier[]>(
      // the URL
      this.command.getUrl(api.DocumentVersions),
      // the parameters
      this.command.getDocumentVersionsOptions(this.user.getUser().UniqueId, masterId))
      // pipe the observable result from 
      // http.get() through an RxJS catchError() operator
      .pipe(
        // tap into the flow of the observable values
        // this allows us to look at the observable values
        // and do something (e.g. log them).
        tap(result => {

          //
          // Log result
          //
          this.log.logMessage(`getAllVersions(); number of versions: ${result.length}`);

          //Convert string date to typed date
          result.forEach(doc => doc.VersionCreatedDate = parseDate(doc.VersionCreated));
        }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Set the legal hold for source document
   * @param sdi SourceDocumentIdentifier for document to set 
   * @param legalHoldStatus Legal hold status detail
   */
  setLegalHold(sdi: SourceDocumentIdentifier, legalHoldStatus: LegalHoldStatus) {
    this.log.logMessage(`setLegalHold(); enter.`);

    if (legalHoldStatus.HoldExpiresDate != undefined) {
      legalHoldStatus.HoldExpires = Utils.getDateToPost(legalHoldStatus.HoldExpiresDate)
    } else {
      legalHoldStatus.HoldExpires = null;
    }

    return this.http.post<SourceDocumentIdentifier>(
      // the URL
      this.command.getUrl(api.DocumentLegalHold),
      // the Body
      legalHoldStatus,
      // the parameters
      this.command.setLegalHoldOptions(this.user.getUser().UniqueId, sdi.MasterId, sdi.Version))
      .pipe(
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Remove the legal hold for a document
   * @param sdi SourcedocumentIdentifier to remove hold
   */
  removeLegalHold(sdi: SourceDocumentIdentifier) {
    this.log.logMessage(`removeLegalHold(); enter`);
    return this.http.delete<SourceDocumentIdentifier>(
      this.command.getUrl(api.DocumentLegalHold),
      this.command.removeLegalHoldOptions(this.user.getUser().UniqueId, sdi.MasterId))
      .pipe(
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Unlock a document 
   * @param sdi SourceDoucmentIdentifier to unlock
   */
  unlockDocument(sdi: SourceDocumentIdentifier) {
    this.log.logMessage(`unlockDocument(); enter`);
    return this.http.delete<SourceDocumentIdentifier>(
      this.command.getUrl(api.DocumentUnlock),
      this.command.getUnlockOptions(this.user.getUser().UniqueId, sdi.MasterId))
      .pipe(
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Fetch the index data for a document
   * @param indexAction IndexAction to perform
   * @param doc DocumentIdentifier for document to fetch
   */
  fetchDocumentIndex(indexAction: number, doc: DocumentIdentifier) {
    this.log.logMessage(`fetchDocumentIndex(); enter`);
    return this.http.get<IndexData[]>(
      this.command.getUrl(api.DocumentIndex),
      this.command.getfetchDocumentIndexOptions(this.user.getUser().UniqueId, indexAction, doc.MasterId, doc.Version, doc.Suffix))
      .pipe(tap(indexData => {
        this.log.logMessage(`fetchDocumentIndex(); fetch document index data`);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }
  /**
    * Get the file information for the document
    * @param doc SourceDocumentIdentifier for document
    */
  getDocumentFileInfo(doc: SourceDocumentIdentifier) {
    this.log.logMessage(`getDocumentFileInfo(); enter`);
    return this.http.get<FileInfo[]>(
      this.command.getUrl(api.DocumentFileInfo),
      this.command.getDocumentFileInfoOptions(this.user.getUser().UniqueId, doc.MasterId, doc.Version))
      .pipe(tap(files => {
        this.log.logMessage(`getDocumentFileInfo(); get document file information`);
        // MIME type asset is set locally
        files.forEach(t => t.mimeTypeURL = Utils.mimeTypeToIcon(t.fileExtension))
        console.log(files);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  /**
    * Get the file information for the data entry folder document
    * @param indexId Document index ID
    * @param isFolderShared folder shared
    */
  getFolderDocumentFileInfo(indexId: number, isFolderShared: boolean) {
    this.log.logMessage(`getFolderDocumentFileInfo(); enter`);
    return this.http.get<FileInfo[]>(
      this.command.getUrl(api.FolderDocumentFiles),
      this.command.getFolderDocumentFileOptions(this.user.getUser().UniqueId, indexId, isFolderShared))
      .pipe(tap(files => {
        this.log.logMessage(`getFolderDocumentFileInfo(); get folder document file information`);
        // MIME type asset is set locally
        files.forEach(t => t.mimeTypeURL = Utils.mimeTypeToIcon(t.fileExtension))
        console.log(files);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Download a blob from the url 
   * @param url URL to file to download
   */
  downloadFile(url: string) {
    return this.http.get(url, { responseType: "blob" })
      .pipe(tap(blob => {
        this.log.logMessage(`downloadFile(); download file to a blob - length ${blob.size}`);
        console.log(url);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err))
            return of([]);
          return this.error.handleError(err);
        })
      );
  }

  /**
   * Create a new document version
   * @param masterId Master Id
   * @param versionLabel Version label
   * @param deviceId Cache device id
   * @param files New file representation
   */
  createDocumentVersion(masterId: number, versionLabel: string, deviceId: number, files: DocumentCreateVersionFile[]) {
    this.log.logMessage(`createDocumentVersion(); enter`);
    return this.http.post<SourceDocumentIdentifier>(
      // the URL
      this.command.getUrl(api.DocumentVersion),
      // the Body
      files,
      // the parpmeters
      this.command.getDocumentNewVersionOptions(this.user.getUser().UniqueId, masterId, versionLabel, deviceId))
      .pipe(tap(result => {
        const sdi = result as SourceDocumentIdentifier;
        this.log.logMessage(`createDocumentVersion(); created version ${sdi.Version} for ${sdi.MasterId}`);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }

  route(title: string, comment: string, templateId: number, routeDetails: RoutingDetails) {
    this.log.logMessage(`sendDocumentToUserorGroup(); enter`);
    return this.http.post<number>(
      // the URL
      this.command.getUrl(api.Route),
      // the Body
      routeDetails,
      // the parpmeters
      this.command.getSendDocumentsToUserOrGroupOptions(this.user.getUser().UniqueId, title, comment, templateId))
      .pipe(tap(result => {
        const routingId = result as number;
        this.log.logMessage(`sendDocumentToUserorGroup(); documents routed - ${routingId}.`);
      }),
        catchError(err => {
          if (this.unauthorized.handleError(err)) {
            this.dialogService.closeAll();
            return of([]);
          }
          return this.error.handleError(err);
        })
      );
  }
}